import * as React from "react";
import { Box, Modal } from "@mui/material";
import { CloseOutlined, ExpandLess, ExpandMore } from "@mui/icons-material";
import { format } from "date-fns";
import useAuthFetch from "../../hooks/useAuthFetch";

const formatValue = (value) => {
  if (value === undefined) return "-";
  if (value === null) return "null";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

// Durations are stored in ms; show them in seconds like the editor does.
// "pages.Qna.faceDetect.gapMs" → "pages.Qna.faceDetect.gap (s)", 5000 → 5.
const toSeconds = (value) =>
  typeof value === "number" ? Number((value / 1000).toFixed(3)) : value;

const displayChange = ({ field, old, new: next }) => {
  const label = typeof field === "string" ? field : "";
  // Already in seconds, e.g. retryCooldownSec → "retryCooldown (s)".
  if (/Sec$/.test(label)) {
    return { field: `${label.replace(/Sec$/, "")} (s)`, old, new: next };
  }
  if (!/Ms$/.test(label)) return { field: label, old, new: next };
  return {
    field: `${label.replace(/Ms$/, "")} (s)`,
    old: toSeconds(old),
    new: toSeconds(next),
  };
};

const formatTimestamp = (ts) => {
  if (!ts) return "-";
  const d = new Date(ts);
  return isNaN(d.getTime()) ? String(ts) : format(d, "dd MMM yyyy, hh:mm a");
};

export default function ProfileHistoryModal({ open, setOpen, profileId, profileName }) {
  const customFetch = useAuthFetch();
  const [history, setHistory] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errMsg, setErrMsg] = React.useState(null);
  // Indexes of the history entries currently expanded; all start collapsed.
  const [expanded, setExpanded] = React.useState(() => new Set());

  const toggleEntry = (index) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  const fetchHistory = async () => {
    setIsLoading(true);
    setErrMsg(null);
    try {
      const url = new URL(
        `${process.env.REACT_APP_API_URL}/v1/dashboard/verificationProfile/history`
      );
      url.searchParams.set("profileId", profileId);
      const { response, status, message } = await customFetch(url, "GET");
      if (status === 200) {
        setHistory(Array.isArray(response?.result) ? response.result : []);
      } else {
        setHistory([]);
        setErrMsg(message || "Something went wrong while fetching history!");
      }
    } catch (error) {
      setHistory([]);
      setErrMsg(error instanceof Error ? error.message : String(error));
    }
    setIsLoading(false);
  };

  React.useEffect(() => {
    if (open && profileId) {
      setExpanded(new Set());
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, profileId]);

  const handleClose = () => setOpen(false);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="config-history-title"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 760,
          maxWidth: "95vw",
          maxHeight: "85vh",
          overflowY: "auto",
          bgcolor: "background.paper",
          boxShadow: 24,
          paddingLeft: 6,
          paddingRight: 6,
          paddingTop: 6,
          paddingBottom: 6,
        }}
        className="rounded-xl relative scrollbar-primary"
      >
        <button className="absolute top-2 right-3" onClick={handleClose}>
          <CloseOutlined className="w-5 h-5  text-gray-450 hover:text-black" />
        </button>
        <p
          id="config-history-title"
          className="text-2xl mb-0 font-extrabold text-center text-gray-850"
        >
          Verification settings history
        </p>
        {profileName && (
          <p className="text-center text-sm text-gray-500 mt-1 mb-0">{profileName}</p>
        )}

        <div className="mt-6 flex flex-col gap-3">
          {isLoading ? (
            <>
              <div className="h-14 w-full bg-gray-300 rounded relative overflow-hidden animate-pulse" />
              <div className="h-14 w-full bg-gray-300 rounded relative overflow-hidden animate-pulse" />
              <div className="h-14 w-full bg-gray-300 rounded relative overflow-hidden animate-pulse" />
            </>
          ) : errMsg ? (
            <p className="text-red-400 text-sm my-1 text-center">{errMsg}</p>
          ) : history.length === 0 ? (
            <p className="text-center text-gray-500 my-4">
              No changes have been made to these settings yet.
            </p>
          ) : (
            history.map((entry, index) => {
              const changes = Array.isArray(entry?.changes)
                ? entry.changes.map(displayChange)
                : [];
              const isOpen = expanded.has(index);
              return (
                <div key={index} className="border-2 border-gray-300 flex flex-col">
                  <button
                    type="button"
                    onClick={() => toggleEntry(index)}
                    aria-expanded={isOpen}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-gray-100 text-left ${
                      isOpen ? "border-b-2 border-gray-300" : ""
                    }`}
                  >
                    <p className="text-sm text-black-150 mb-0">
                      <strong>{entry?.Action || "UPDATE"}</strong> by{" "}
                      <strong>{entry?.["Changed By"] || "-"}</strong>
                      <span className="text-gray-500">
                        {" · "}
                        {formatTimestamp(entry?.Timestamp)}
                      </span>
                    </p>
                    <span className="flex items-center gap-1 text-xs text-gray-500 shrink-0">
                      {changes.length} change{changes.length === 1 ? "" : "s"}
                      {isOpen ? (
                        <ExpandLess className="w-5 h-5 text-black-150" />
                      ) : (
                        <ExpandMore className="w-5 h-5 text-black-150" />
                      )}
                    </span>
                  </button>
                  {!isOpen ? null : changes.length === 0 ? (
                    <p className="px-3 py-2 text-sm text-gray-500 mb-0">
                      No field changes recorded.
                    </p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-black-150">
                          <th className="px-3 py-1.5 font-semibold">Field</th>
                          <th className="px-3 py-1.5 font-semibold">Old</th>
                          <th className="px-3 py-1.5 font-semibold" />
                          <th className="px-3 py-1.5 font-semibold">New</th>
                        </tr>
                      </thead>
                      <tbody>
                        {changes.map((change, i) => (
                          <tr key={i} className="border-t border-gray-300 align-top">
                            <td className="px-3 py-1.5 font-mono break-all">{change.field}</td>
                            <td className="px-3 py-1.5 text-red-500 break-all">
                              {formatValue(change.old)}
                            </td>
                            <td className="px-1 py-1.5 text-gray-500">→</td>
                            <td className="px-3 py-1.5 text-green-600 break-all">
                              {formatValue(change.new)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })
          )}
        </div>
      </Box>
    </Modal>
  );
}
